/**
 * Lesson Image Generation — fan-out stage.
 * Generates images from descriptions in the lesson content using Imagen.
 * Editable prompt per image, presigned URL rendering.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { LessonStagePanel } from '../../shared/LessonStagePanel';
import { useLessonContext } from '../../LessonContext';
import { useLessonBatchExecution } from '../../shared/useLessonBatchExecution';
import { EmptyStageNotice } from '../../shared/EmptyStageNotice';
import { getPresignedUrl } from '../../../../services/courseRunsService';

interface ImageSpec {
  image_id: string;
  section_title: string;
  description: string;
  alt_text: string;
}

function collectImageSpecs(lessonContent: any): ImageSpec[] {
  const specs: ImageSpec[] = [];
  for (const section of lessonContent?.sections || []) {
    for (const item of section.content || []) {
      if (item.type === 'image') {
        specs.push({
          image_id: `img_${specs.length}`,
          section_title: section.title || '(Opening)',
          description: item.description || '',
          alt_text: item.alt_text || '',
        });
      }
    }
  }
  return specs;
}

function PresignedImage({ s3Key, alt, className, refreshKey }: { s3Key: string; alt: string; className?: string; refreshKey?: number }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const presigned = await getPresignedUrl(s3Key);
        if (!cancelled) setUrl(presigned);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load image');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [s3Key, refreshKey]);

  if (loading) return <div className="flex items-center justify-center py-12 text-xs text-gray-400">Loading image...</div>;
  if (error) return <div className="text-xs text-red-500 py-4">{error}</div>;
  if (!url) return null;

  return <img src={url} alt={alt} className={className || 'max-w-full max-h-96 rounded'} />;
}

function ImageCards({ onSelectedIdChange, refreshCounter, onGenerated }: { onSelectedIdChange: (id: string | null) => void; refreshCounter: number; onGenerated: () => void }) {
  const { document } = useLessonContext();
  const lessonContent: any = document?.lesson_content;
  const images: any = document?.images || {};
  const specs = collectImageSpecs(lessonContent);

  const {
    selectedId, setSelectedId: setSelectedIdInternal,
    runningAll, batchStatus, runningSingle,
    runSingle, runAll, cancelBatch,
  } = useLessonBatchExecution({ stageId: 'lesson_image_gen' });

  // Editable description per image
  const [editedDescriptions, setEditedDescriptions] = useState<Record<string, string>>({});

  const setSelectedId = useCallback((id: string | null) => {
    setSelectedIdInternal(id);
    onSelectedIdChange(id);
  }, [setSelectedIdInternal, onSelectedIdChange]);

  const selectedSpec = specs.find((s) => s.image_id === selectedId);
  const selectedImage = selectedId ? images[selectedId] : null;

  const currentDescription = selectedId
    ? (editedDescriptions[selectedId] ?? selectedSpec?.description ?? '')
    : '';

  return (
    <div className="space-y-3">
      {/* Batch controls */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#1a2332' }}
          disabled={runningAll || specs.length === 0}
          onClick={async () => { await runAll(specs.map((s) => s.image_id)); onGenerated(); }}
        >
          {runningAll ? 'Generating All...' : `Generate All (${specs.length})`}
        </button>
        {runningAll && (
          <button type="button" className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50" onClick={cancelBatch}>Cancel</button>
        )}
        <span className="text-xs text-gray-400">
          {Math.max(
            Object.keys(images).length,
            Object.values(batchStatus).filter((s: any) => s.status === 'succeeded').length
          )}/{specs.length} generated
        </span>
      </div>

      {/* Image spec cards */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {specs.map((spec) => {
          const hasImage = !!images[spec.image_id];
          const imgData = images[spec.image_id];
          const batch = batchStatus[spec.image_id];
          return (
            <button
              key={spec.image_id}
              type="button"
              className={`flex-shrink-0 border rounded text-left text-xs w-52 overflow-hidden ${
                selectedId === spec.image_id ? 'ring-2 ring-blue-500 border-blue-300' : 'border-gray-200'
              } ${hasImage ? 'bg-green-50' : 'bg-white'}`}
              onClick={() => setSelectedId(spec.image_id)}
            >
              {/* Thumbnail */}
              {hasImage && imgData?.s3_keys?.[0] && (
                <div className="h-24 bg-gray-100 overflow-hidden">
                  <PresignedImage s3Key={imgData.s3_keys[0]} alt={spec.description} className="w-full h-full object-cover" refreshKey={refreshCounter} />
                </div>
              )}
              <div className="px-3 py-2">
                <div className="font-medium text-gray-800 truncate">{spec.section_title}</div>
                <div className="text-gray-500 mt-0.5 truncate">{spec.description.slice(0, 60)}...</div>
                {batch && (
                  <div className={`text-[10px] mt-1 ${batch.status === 'succeeded' ? 'text-green-600' : batch.status === 'failed' ? 'text-red-600' : 'text-blue-600'}`}>
                    {batch.status}
                  </div>
                )}
                {!batch && hasImage && (
                  <div className="text-[10px] mt-1 text-green-600">generated</div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected image detail */}
      {selectedSpec && (
        <div className="border rounded p-3 bg-white space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-medium text-gray-800">{selectedSpec.section_title}</div>
            <button
              type="button"
              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
              disabled={runningSingle || runningAll}
              onClick={async () => { await runSingle(selectedSpec.image_id); onGenerated(); }}
            >
              {runningSingle ? 'Generating...' : selectedImage ? 'Regenerate' : 'Generate'}
            </button>
          </div>

          {/* Editable description */}
          <div>
            <label className="text-[10px] font-bold uppercase text-gray-400 mb-1 block">Image Prompt</label>
            <textarea
              className="w-full text-xs border rounded p-2 bg-gray-50 focus:bg-white focus:ring-1 focus:ring-blue-300 focus:border-blue-300 resize-y"
              rows={3}
              value={currentDescription}
              onChange={(e) => setEditedDescriptions((prev) => ({ ...prev, [selectedSpec.image_id]: e.target.value }))}
              placeholder="Describe the image to generate..."
            />
            {editedDescriptions[selectedSpec.image_id] !== undefined &&
              editedDescriptions[selectedSpec.image_id] !== selectedSpec.description && (
              <div className="text-[10px] text-amber-600 mt-1">
                Prompt edited (original will be used for batch generation)
              </div>
            )}
          </div>

          {/* Generated image */}
          {selectedImage && selectedImage.s3_keys && selectedImage.s3_keys.length > 0 ? (
            <div className="space-y-2">
              {selectedImage.prompt && (
                <div className="text-[10px] text-gray-400">
                  Generated with: <span className="text-gray-600">{selectedImage.prompt.slice(0, 200)}{selectedImage.prompt.length > 200 ? '...' : ''}</span>
                </div>
              )}
              <div className="bg-gray-100 rounded-lg p-2 flex items-center justify-center">
                <PresignedImage
                  s3Key={selectedImage.s3_keys[0]}
                  alt={selectedSpec.alt_text || selectedSpec.description}
                  refreshKey={refreshCounter}
                />
              </div>
              <div className="text-[10px] text-gray-400">
                S3: {selectedImage.s3_keys[0]}
              </div>
            </div>
          ) : selectedImage ? (
            <div className="text-xs text-gray-500">Image generated but no S3 keys found.</div>
          ) : (
            <div className="text-xs text-gray-400 italic">Not yet generated. Edit the prompt above if needed, then click Generate.</div>
          )}
        </div>
      )}
    </div>
  );
}

function renderOutput(doc: Record<string, unknown>) {
  const images = (doc.images as any) || {};
  const count = Object.keys(images).length;
  if (count === 0) return <div className="text-sm text-gray-500">No images generated yet.</div>;
  return <div className="text-xs text-gray-600">{count} images generated</div>;
}

export function LessonImageGen() {
  const { document } = useLessonContext();
  const lessonContent = document?.lesson_content as any;
  const hasLessonContent = !!lessonContent?.sections?.length;
  const specs = hasLessonContent ? collectImageSpecs(lessonContent) : [];
  const [extractionId, setExtractionId] = React.useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = React.useState(0);

  if (hasLessonContent && specs.length === 0) {
    return <EmptyStageNotice stageId="lesson_image_gen" contentType="images" />;
  }

  return (
    <LessonStagePanel
      stageId="lesson_image_gen"
      title="Image Generation"
      description="Generate images from descriptions in the lesson content using the course image style guide."
      hideRunButton
      renderOutput={renderOutput}
      extractionId={extractionId || undefined}
    >
      {hasLessonContent ? <ImageCards onSelectedIdChange={setExtractionId} refreshCounter={refreshCounter} onGenerated={() => setRefreshCounter((c) => c + 1)} /> : (
        <div className="text-xs text-gray-500">No lesson content. Generate and review the lesson first.</div>
      )}
    </LessonStagePanel>
  );
}
